/**
 * Abstract types for Reports V2
 *
 * Design principles:
 * - All metrics are comparable (current vs previous)
 * - Two types: absolute numbers and time series
 * - Generic to support any metric type
 */

export type { OperationsData } from "./operations-types";
export type { OverviewData } from "./overview-types";
export type { WebData } from "./web-types";

/**
 * Time range presets
 */
export enum TimeRangePreset {
  LAST_7_DAYS = "LAST_7_DAYS",
  LAST_30_DAYS = "LAST_30_DAYS",
  LAST_90_DAYS = "LAST_90_DAYS",
  LAST_WEEK = "LAST_WEEK",
  LAST_MONTH = "LAST_MONTH",
  LAST_QUARTER = "LAST_QUARTER",
  LAST_YEAR = "LAST_YEAR",
  SPECIFIC_MONTH = "SPECIFIC_MONTH",
  CUSTOM = "CUSTOM",
}

/**
 * Identifies a single calendar month (1-12 = Jan-Dec) used by the
 * SPECIFIC_MONTH preset.
 */
export interface SpecificMonth {
  year: number;
  month: number;
}

/**
 * Comparison strategy - how to calculate comparison period
 */
export enum ComparisonStrategy {
  PREVIOUS_PERIOD = "PREVIOUS_PERIOD", // Sequential (last 30 days vs 30 days before)
  SAME_PERIOD_LAST_YEAR = "SAME_PERIOD_LAST_YEAR", // Year-over-year (for seasonality)
  CUSTOM = "CUSTOM", // User-defined comparison period
}

/**
 * Time period definition
 */
export interface TimePeriod {
  startDate: Date;
  endDate: Date;
  label: string; // Human-readable label (e.g., "Dec 15 - Jan 14, 2025")
}

/**
 * Time range configuration
 */
export interface TimeRangeConfig {
  preset: TimeRangePreset;
  customRange?: TimePeriod; // Only used when preset = CUSTOM
  specificMonth?: SpecificMonth; // Only used when preset = SPECIFIC_MONTH
  comparisonStrategy: ComparisonStrategy;
  customComparisonRange?: TimePeriod; // Only used when comparisonStrategy = CUSTOM
}

/**
 * Comparable value - wraps current and previous values with metadata
 * Generic type T for the actual value (number, string, etc.)
 */
export interface ComparableValue<T> {
  current: T;
  previous: T;
  change: number; // Absolute change (current - previous)
  changePercent: number; // Percentage change ((current - previous) / previous * 100)
  isIncrease: boolean; // True if current > previous
  isPositive: boolean; // True if change is good (context-dependent)
  trend?: { value: number; date?: string }[]; // Daily data points for sparkline with optional date labels
}

/**
 * Abstract metric - single absolute number with comparison
 * Example: Total Revenue, Customer Count, Average Rating
 */
export interface AbsoluteMetric<T = number> {
  type: "absolute";
  label: string;
  value: ComparableValue<T>;
  format?: "number" | "currency" | "percentage" | "duration"; // How to display
  inverse?: boolean; // If true, decrease is good (e.g., no-show rate)
}

/**
 * Time series data point
 */
export interface TimeSeriesPoint<T = number> {
  timestamp: Date;
  label: string; // Formatted date/time for display
  value: T;
}

/**
 * Abstract time series metric - values over time with comparison
 * Example: Daily Revenue, Weekly Bookings, Hourly Capacity
 */
export interface TimeSeriesMetric<T = number> {
  type: "timeseries";
  label: string;
  current: TimeSeriesPoint<T>[]; // Current period data
  previous: TimeSeriesPoint<T>[]; // Comparison period data
  format?: "number" | "currency" | "percentage" | "duration";
}

/**
 * Helper type - create comparable value from current and previous
 */
export function createComparableValue<T extends number>(
  current: T,
  previous: T,
  inverse = false,
  isPercentageValue = false
): ComparableValue<T> {
  const change = current - previous;
  // For percentage values, show absolute difference (percentage points)
  // For regular values, show percentage change
  const changePercent = isPercentageValue
    ? change
    : previous === 0
      ? current > 0
        ? 100
        : 0
      : (change / previous) * 100;
  const isIncrease = current > previous;
  const isPositive = inverse ? !isIncrease : isIncrease;

  return {
    current,
    previous,
    change,
    changePercent,
    isIncrease,
    isPositive,
  };
}

/**
 * Format helpers
 */
export const formatValue = (
  value: number,
  format: "number" | "currency" | "percentage" | "duration" = "number"
): string => {
  switch (format) {
    case "currency":
      return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "percentage":
      return `${value.toFixed(1)}%`;
    case "duration":
      return `${Math.round(value)}m`;
    default:
      return value.toLocaleString();
  }
};
